"use client";

import { Fragment, type ReactNode } from "react";
import Image from "next/image";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { CTAButton, type CTAButtonProps } from "@/shadcn/ui/button";
import { ArrowIcon } from "./_shared";
import { IFooterColumn, IFooterLink } from "@/sanity/pages/PageHomeTemplate";
import logoWCE from "@/public/global/logo.svg";
import { cn } from "@/lib/utils";
import { HubSpotCookieButton } from "@/components/global/ui/HubSpotCookieButton";

interface FooterSectionProps {
  id?: string;
  logo?: GROQImage;
  headline?: ReactNode;
  subtext?: ReactNode;
  cta?: GROQButton;
  columns?: IFooterColumn[];
  copyright?: string;
  legalLinks?: IFooterLink[];
  ctaButtonClassName?: string;
  activeLinkUrl?: string;
  headlineEmphasis?: string;
  ctaFallbackLabel?: string;
  ctaIconOverride?: CTAButtonProps["iconOverride"];
  className?: string;
  logoClassName?: string;
  /** Show the back-to-top button on mobile too, in the top-right corner (hidden there by default). */
  showMobileBackToTop?: boolean;
}

export default function FooterSection({
  id,
  logo,
  headline,
  subtext,
  cta,
  columns,
  copyright,
  legalLinks,
  ctaButtonClassName,
  activeLinkUrl,
  headlineEmphasis,
  ctaFallbackLabel,
  ctaIconOverride,
  className,
  logoClassName,
  showMobileBackToTop = false,
}: FooterSectionProps) {
  const logoSource = logo?.src ?? logoWCE;
  const logoWidth = logo?.width ?? 252;
  const logoHeight = logo?.height ?? 38;
  const isActiveLink = (link: IFooterLink) => Boolean(activeLinkUrl && link.url === activeLinkUrl);
  const headlineContent =
    typeof headline === "string" && headlineEmphasis && headline.includes(headlineEmphasis) ? (
      <>
        {headline.slice(0, headline.indexOf(headlineEmphasis))}
        <span className="font-medium italic">{headlineEmphasis}</span>
        {headline.slice(headline.indexOf(headlineEmphasis) + headlineEmphasis.length)}
      </>
    ) : (
      headline
    );

  return (
    <footer
      id={id}
      className={cn("w-full bg-[#0F0F0F]", className)}>
      {/* ───────────────────────── Desktop ───────────────────────── */}
      <div className="hidden lg:block pt-[7.5rem] w-[min(149.75rem,100%)] mx-auto">
        <div className="flex flex-col items-end w-full gap-[21.5rem]">
          <div className="flex justify-between items-start w-full">
            {/* brand + contact */}
            <div className="flex w-[min(44.25rem,100%)] flex-col gap-[4rem] ">
              <Image
                src={logoSource}
                alt={logo?.name ?? "World Class Edge"}
                width={logoWidth}
                height={logoHeight}
                className={cn("h-auto w-[31.5rem]", logoClassName)}
              />
              <div className="flex flex-col gap-[3rem]">
                <div className="flex flex-col gap-[0.5rem]">
                  {headline && (
                    <div className="m-0 text-white font-geist text-[2.25rem] leading-[1.5] tracking-[-0.09rem]">
                      {headlineContent}
                    </div>
                  )}
                  {subtext && (
                    <div className="m-0 text-white opacity-50 font-geist text-[2rem] leading-[1.5] tracking-[-0.08rem]">
                      {subtext}
                    </div>
                  )}
                </div>
                <div className="w-full">
                  <CTAButton
                    data={cta ?? {}}
                    overrideText={ctaFallbackLabel}
                    iconOverride={ctaIconOverride}
                    buttonClassName={ctaButtonClassName}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-[5rem]">
              <div className="flex items-start gap-[5rem]">
                {columns?.map((col, i) => (
                  <div
                    key={i}
                    className="flex flex-col min-w-[15.625rem] gap-[1.25rem]">
                    <p className="m-0 text-white opacity-40 font-geist text-[2.25rem] leading-[3rem]">{col.heading}</p>
                    <div className="flex flex-col gap-[1.5rem]">
                      {col.links?.map((link, j) => (
                        <a
                          key={j}
                          href={link.url || "#"}
                          className={cn(
                            "text-white transition hover:opacity-100 font-geist text-[2.25rem] leading-[3rem]",
                            isActiveLink(link)
                              ? "font-semibold opacity-90 underline"
                              : "font-normal opacity-60",
                          )}>
                          {link.label}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <a
                href="#top"
                aria-label="Back to top"
                className="flex justify-center items-center text-black bg-white transition rounded-[1.25rem] w-[6rem] h-[6rem] hover:brightness-95">
                <span className="-rotate-90">
                  <ArrowIcon />
                </span>
              </a>
            </div>
          </div>

          <div className="flex justify-between items-center w-full border-t h-[9rem] border-white/10 py-[3rem]">
            {copyright && (
              <p className="text-white font-geist text-[1.75rem] leading-[3rem] opacity-[0.64]">{copyright}</p>
            )}
            <div className="flex items-center gap-[1.75rem]">
              {legalLinks?.map((link, i) => (
                <span
                  key={i}
                  className="flex items-center gap-[1.75rem]">
                  {i > 0 && <span className="rounded-full h-[0.75rem] w-[0.75rem] bg-white/40" />}
                  {link.url === "#cookie-settings" ? (
                    <HubSpotCookieButton className="text-white opacity-60 transition hover:opacity-100 font-geist text-[1.75rem] leading-[3rem]">
                      {link.label}
                    </HubSpotCookieButton>
                  ) : (
                    <a
                      href={link.url || "#"}
                      className="text-white opacity-60 transition hover:opacity-100 font-geist text-[1.75rem] leading-[3rem]">
                      {link.label}
                    </a>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ───────────────────────── Mobile ───────────────────────── */}
      <div className="flex flex-col lg:hidden gap-[10rem] px-[2rem] pb-[2.25rem] pt-[5.5rem]">
        <div className="flex flex-col gap-[7rem]">
          {/* brand + contact + back-to-top */}
          <div className={`flex justify-between items-start gap-[1.5rem]${showMobileBackToTop ? " relative" : ""}`}>
            <div className="flex flex-col flex-1 gap-[4rem]">
              <Image
                src={logoSource}
                alt={logo?.name ?? "World Class Edge"}
                width={logoWidth}
                height={logoHeight}
                className={cn("h-auto w-[31.5rem]", logoClassName)}
              />
              <div className="flex flex-col gap-[2.75rem]">
                <div className="flex flex-col gap-[0.5rem]">
                  {headline && (
                    <div className="m-0 text-white font-geist text-[2.25rem] leading-[1.5] tracking-[-0.09rem]">{headlineContent}</div>
                  )}
                  {subtext && (
                    <div className="m-0 text-white opacity-50 font-geist text-[2rem] leading-[1.5] tracking-[-0.08rem]">
                      {subtext}
                    </div>
                  )}
                </div>
                <div className="w-full">
                  <CTAButton
                    data={cta ?? {}}
                    overrideText={ctaFallbackLabel}
                    iconOverride={ctaIconOverride}
                    buttonClassName={ctaButtonClassName}
                    fullWidth
                  />
                </div>
              </div>
            </div>
            <a
              href="#top"
              aria-label="Back to top"
              className={
                showMobileBackToTop
                  ? "absolute top-0 right-0 flex h-[6rem] w-[6rem] items-center justify-center rounded-[1.25rem] bg-white text-black transition hover:brightness-95"
                  : "hidden justify-center items-center text-black bg-white transition lg:flex shrink-0 rounded-[1.25rem] px-[1.5rem] py-[1.5rem] hover:brightness-95"
              }>
              <span className="-rotate-90">
                <ArrowIcon />
              </span>
            </a>
          </div>

          {/* link rows */}
          <div className="flex flex-col gap-[3.5rem]">
            {columns?.map((col, i) => (
              <Fragment key={i}>
                <div className="flex justify-between items-start gap-[2rem]">
                  <p className="m-0 text-white opacity-40 font-geist text-[2.25rem] leading-[3rem]">{col.heading}</p>
                  <div className="flex flex-col items-start gap-[1.5rem] w-[16rem] shrink-0">
                    {col.links?.map((link, j) => (
                      <a
                        key={j}
                        href={link.url || "#"}
                        className={cn(
                          "text-white transition hover:opacity-100 font-geist text-[2.25rem] leading-[3rem]",
                          isActiveLink(link)
                            ? "font-semibold opacity-90 underline"
                            : "font-normal opacity-60",
                        )}>
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="w-full h-px bg-white/10" />
              </Fragment>
            ))}
          </div>
        </div>

        {/* bottom — legal links + copyright (centered) */}
        <div className="flex flex-col items-center gap-[1.75rem]">
          <div className="flex w-[39.25rem] max-w-full flex-wrap content-center justify-center items-center gap-[1rem]">
            {legalLinks?.map((link, i) => (
              <span
                key={i}
                className="flex items-center gap-[1rem]">
                {i % 2 === 1 && <span className="rounded-full h-[0.75rem] w-[0.75rem] bg-[#98D8FF]" />}
                {link.url === "#cookie-settings" ? (
                  <HubSpotCookieButton className="text-center transition font-geist text-[2rem] leading-[3rem] text-white/50 hover:text-white/100">
                    {link.label}
                  </HubSpotCookieButton>
                ) : (
                  <a
                    href={link.url || "#"}
                    className="text-center transition font-geist text-[2rem] leading-[3rem] text-white/50 hover:text-white/100">
                    {link.label}
                  </a>
                )}
              </span>
            ))}
          </div>
          <div className="w-full h-px bg-white/10" />
          {copyright && <p className="text-center font-geist text-[2rem] leading-[3rem] text-white/50">{copyright}</p>}
        </div>
      </div>
    </footer>
  );
}
